import { ref } from 'vue'
import { useQuoteDetailFormations } from './useQuoteDetailFormations.js'

/**
 * Jeu de données d'exemple pour le mode aperçu du tunnel « Analyse du besoin ».
 *
 * Reproduit la surface publique de useQuoteDetailPage (les clés destructurées
 * par QuoteBesoinTunnel.vue). La demande est factice et rien n'est enregistré :
 * les handlers d'écriture restent no-op.
 *
 * Le catalogue, lui, est le vrai. Un aperçu qui montrerait des formations
 * inventées n'apprendrait rien : c'est en chargeant `useQuoteDetailFormations`
 * — le composable de la vraie fiche — que l'aperçu affiche les mêmes
 * catégories, les mêmes durées, les mêmes tailles de groupe et les mêmes
 * tarifs, et que les listes déroulantes réagissent comme en production.
 * Réutiliser le composable plutôt que le recopier supprime du même coup le
 * risque de dérive entre ce miroir et l'original.
 */
export function usePreviewQuoteDetail() {
  const sampleQuote = {
    id: 'apercu',
    human_id: 'DEMO',
    source: 'Aperçu',
    client_type: 'professionnel',
    // Code civilité (mr/mme/autre) attendu par le <select>, pas le libellé.
    civilite: 'mme',
    nom: 'Martin',
    prenom: 'Camille',
    email: 'camille.martin@exemple.fr',
    telephone: '06 12 34 56 78',
    professionnel_nom_etablissement: 'Entreprise Exemple SAS',
    professionnel_siret: '123 456 789 00012',
    professionnel_adresse: '12 rue de la Prévention',
    professionnel_code_postal: '75011',
    professionnel_ville: 'Paris',
    professionnel_activite_principale: 'Conseil aux entreprises',
    professionnel_est_public: false,
    professionnel_tva: true,
    professionnel_numero_tva: 'FR 12 345678900',
    // Statut santé entreprise : chaîne parmi actif/ferme/radie/en_difficulte/
    // procedure_collective (jamais un booléen — cf. CompanySanteBadges).
    company_sante: 'actif',
    formation_categorie: 'INCENDIE',
    // Pas de `formation_catalogue_id` en dur : il ne désignerait rien dans le
    // vrai catalogue. useQuoteDetailFormations hydrate la liste à partir du
    // nom quand celui-ci ne désigne qu'une seule formation.
    formation_nom: 'Formation EPI Equipier de Première Intervention - MEX et évacuation',
    formation_lieu: 'INTRA',
    lieu_formation_adresse: '12 rue de la Prévention',
    lieu_formation_code_postal: '75011',
    lieu_formation_ville: 'Paris',
    lieu_formation_commentaire: 'Accès par la cour intérieure, 2e étage.',
    stationnement_formateur: 'parking_prive',
    intra_nombre_participants: 8,
    emplacement_exterieur_bac_flamme: null,
    emplacement_camion_unite_mobile: null,
    dates_disponibilites: [],
    dates_inconnues: false,
    financement: 'Plan de développement des compétences'
  }

  const previewQuote = ref(sampleQuote)
  const previewEdited = ref({ ...sampleQuote })
  const isEditing = ref(true)
  const previewGetValue = (field) => previewEdited.value?.[field] ?? previewQuote.value?.[field]
  const noop = () => {}

  const formations = useQuoteDetailFormations({
    quote: previewQuote,
    isEditing,
    editedQuote: previewEdited,
    getValue: previewGetValue
  })
  // Comme useQuoteDetailPageInit sur une vraie demande : le catalogue se charge
  // à l'ouverture. L'échec éventuel ne doit pas casser l'aperçu, qui reste
  // consultable avec des listes vides.
  Promise.resolve(formations.loadFormationsData()).catch(() => {})

  return {
    quote: previewQuote,
    loading: ref(false),
    isEditing,
    editedQuote: previewEdited,
    saving: ref(false),
    canEdit: ref(true),
    isQuoteSigned: ref(false),
    startEditing: noop,
    saveQuote: async () => {},
    getValue: previewGetValue,
    loadQuote: noop,
    ...formations,
    handleDatesDisponibilitesChange: noop,
    handleDatesPeriodesChange: noop,
    handleDatesInconnuesChange: noop
  }
}
